/**
 * docs.tryairlock.app — one ordering, one list of its own pages, drawn where
 * there is room for it.
 *
 * `/` is the table of contents; `/<chapter>` is a chapter, with the same list
 * sticking beside it on a window wide enough to hold both. The article is HTML
 * the build made from `content/<id>.md`, and its number, its neighbours in the
 * pager and what the search matches all come out of that file's frontmatter —
 * so adding a chapter is adding a file.
 */
import { useMemo, useRef, useState } from 'react';
import type React from 'react';
import { ContentsSheet, useContentsSheet } from './components/ContentsSheet';
import { BetaTag, ChapterRail, Contents, ManualHeader, Pager, useRailTop } from './components/DocsChrome';
import { Lightbox, useZoomableShots } from './components/Lightbox';
import { useCopyButtons } from './lib/copy';
import { DOCS, filterDocs, findDoc } from './lib/content';
import { useChapterFade, useReveal } from './lib/motion';
import { CONTENTS, FRONT, useDocHead, useDocRoute } from './lib/router';

export default function App(): React.JSX.Element {
  const { id, go } = useDocRoute();
  const [query, setQuery] = useState('');

  const sidebar = useRef<HTMLElement>(null);
  const railTop = useRailTop(sidebar);
  const prose = useRef<HTMLDivElement>(null);
  const article = useRef<HTMLElement>(null);
  const sheet = useContentsSheet();

  // `/` is the first chapter, not the contents: a manual opens on its first
  // page, and the list of fifty is a page of its own behind the pager.
  const current = id === CONTENTS ? undefined : findDoc(id || FRONT);
  const index = current ? DOCS.indexOf(current) : -1;
  const listed = useMemo(() => filterDocs(query), [query]);
  // A capture is drawn at the size it was taken and capped by the column; the
  // ones the column shrank open at full size on a click.
  const zoom = useZoomableShots(prose, current?.html ?? '');
  useCopyButtons(prose, current?.html ?? '');
  // Motion, all of it off under reduced motion: captures and tables rise as
  // they are reached, and a new chapter fades up in place of the last.
  useReveal(prose, current?.html ?? '');
  useChapterFade(article, current?.id ?? CONTENTS);

  useDocHead(
    current
      ? { title: current.title, description: current.lede, id: current.id }
      : {
          title: 'Contents',
          description: 'Every chapter of the Airlock manual, in the order it is worth reading.',
          id: CONTENTS
        }
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--doc-bg)', color: 'var(--doc-text)' }}>
      <ManualHeader onGo={go} sheetOpen={sheet.open} onSheet={sheet.toggle} />
      <ContentsSheet docs={DOCS} current={current?.id} open={sheet.open} onClose={sheet.close} onGo={go} />

      <main>
        {current ? (
          <div className="al-manual al-manual-chapter">
            {/* The rail lists every chapter: the search lives on the contents page. */}
            <aside className="al-sidebar" ref={sidebar} style={{ ['--al-rail-top' as string]: `${railTop}px` }}>
              <ChapterRail docs={DOCS} current={current.id} onGo={go} />
            </aside>

            <article className="al-chapter" ref={article}>
              <div className="al-label al-eyebrow">
                {current.part} · Chapter {current.order}
              </div>
              <h1 className="al-h1">
                {current.title}
                {current.beta ? <BetaTag /> : null}
              </h1>
              {/* Build output from this repository's own markdown — never a request's. */}
              <div ref={prose} className="al-prose al-prose-docs" dangerouslySetInnerHTML={{ __html: current.html }} />
              <Pager previous={DOCS[index - 1]} next={DOCS[index + 1]} onGo={go} />
            </article>
          </div>
        ) : (
          <div className="al-manual al-manual-index">
            <Contents docs={listed} total={DOCS.length} query={query} onQuery={setQuery} onGo={go} />
          </div>
        )}
      </main>

      {zoom.shot ? <Lightbox shot={zoom.shot} onClose={zoom.close} /> : null}
    </div>
  );
}
